/* ============================================================================
   proton-nav.js — hover-to-open mega menu, panel CTA, NEW pills, utility bar.
   Source of truth: ~/proton/public/storefront/proton-nav.js
   Deployed by:     ~/proton/scripts/_deploy-proton-nav.js
   ----------------------------------------------------------------------------
   WHY THIS EXISTS
   Dawn's mega menu is a <details>/<summary>. It opens on CLICK only. Diego's
   ask was explicitly "when I HOVER over Pickleball". So we drive the same
   `open` attribute from pointer events, which means Dawn's own CSS transition,
   its <header-menu> element and every keyboard path keep working unchanged —
   we are not reimplementing the menu, only adding a second way to open it.

   🚨 Hover is bound ONLY under (hover:hover) and (pointer:fine). On a touch
      device mouseenter fires on FIRST TAP, so a hover-open menu eats the tap
      that was meant to follow the link.
   ========================================================================== */
(function () {
  "use strict";

  var CLOSE_DELAY = 180; // ms of grace when the pointer crosses the seam
  var CTA_LABEL_MAX = 42;
  var PROMO_MAX_COLS = 4; // above this the tile is dropped — see addPromo()

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* ── 1. NEW pills ────────────────────────────────────────────────────────
     A menu item titled "Project Coyote — New" becomes "Project Coyote" + a
     pill. Done in JS so the badge lives in ONE place: the menu title. Adding a
     badge to another item is a Shopify nav edit, not a code change. */
  function decorateNew(root) {
    var links = root.querySelectorAll(
      ".mega-menu__link, .header__menu-item, .menu-drawer__menu-item"
    );
    Array.prototype.forEach.call(links, function (a) {
      if (a.dataset.pnavNew === "1") return;
      var host = a.querySelector("span") || a;
      // only touch a node whose whole text ends with the marker
      var m = /^(.*?)\s*[—–-]\s*New\s*$/i.exec(host.textContent.trim());
      if (!m || !m[1]) return;
      a.dataset.pnavNew = "1";
      host.textContent = m[1];
      var pill = document.createElement("span");
      pill.className = "pnav-new";
      pill.textContent = "NEW";
      host.appendChild(pill);
    });
  }

  /* ── 2. panel CTA ────────────────────────────────────────────────────────
     The reference has a primary button at the foot of the panel. We derive it
     from the FIRST column's heading link, which is always the section's own
     shop-all destination, so nothing is hard-coded and it cannot drift from
     the menu. Only rendered for a COLLECTION destination — "Shop all About"
     would be nonsense. */
  function addCta(details) {
    var content = details.querySelector(".mega-menu__content");
    var list = content && content.querySelector(".mega-menu__list");
    if (!list || content.querySelector(".pnav-cta-row")) return;

    var firstCol = list.querySelector(":scope > li > .mega-menu__link--level-2");
    if (!firstCol) return;
    var href = firstCol.getAttribute("href") || "";
    if (href.indexOf("/collections/") === -1) return;

    var summarySpan = details.querySelector("summary span");
    var label = (summarySpan ? summarySpan.textContent : "").trim();
    if (!label || label.length > CTA_LABEL_MAX) return;

    // 🚨 `page-width` is load-bearing, not decoration. `.mega-menu__list` carries
    //    Dawn's page-width padding; a plain sibling <div> does NOT, so the buttons
    //    sat flush against the panel edge while every column above them started
    //    ~78px in. Same class = same gutter, at every breakpoint, for free.
    var row = document.createElement("div");
    row.className = "pnav-cta-row page-width";

    var cta = document.createElement("a");
    cta.className = "pnav-cta";
    cta.href = href;
    cta.textContent = "Shop all " + label;
    row.appendChild(cta);

    // ⛔ ONE CTA PER PANEL — Diego, 2026-09-09: "multiple CTAs is too hard."
    //    A secondary ghost button ("How our paddles are built" → /pages/science)
    //    shipped here and was cut. The page is NOT orphaned: "The Science" is
    //    still a link in this same menu's Explore column. Do not re-add a second
    //    button to this row.
    list.insertAdjacentElement("afterend", row);
  }

  /* ── 2b. promo tile ──────────────────────────────────────────────────────
     Config comes from theme.liquid (real asset_url values). A menu with no
     entry simply gets no tile. */
  function addPromo(details) {
    var cfg = (window.PROTON_NAV && window.PROTON_NAV.promos) || null;
    if (!cfg) return;
    var content = details.querySelector(".mega-menu__content");
    var list = content && content.querySelector(".mega-menu__list");
    if (!list || list.querySelector(".pnav-promo")) return;

    var span = details.querySelector("summary span");
    var key = (span ? span.textContent : "").trim().toLowerCase();
    var p = cfg[key];
    if (!p || !p.img || !p.href) return;

    // 🚨 A promo eats ~360px. Measured: with 6 link columns that leaves ~90px
    //    per column, which wraps the last column onto a second row and opens a
    //    dead half-row under the panel. Columns win — the tile is decoration.
    if (list.querySelectorAll(":scope > li").length > PROMO_MAX_COLS) return;

    var a = document.createElement("a");
    a.className = "pnav-promo";
    a.href = p.href;

    var img = document.createElement("img");
    img.className = "pnav-promo__img";
    img.src = p.img;
    img.alt = "";
    img.loading = "lazy";
    img.setAttribute("aria-hidden", "true");
    a.appendChild(img);

    var body = document.createElement("div");
    body.className = "pnav-promo__body";
    if (p.eyebrow) {
      var e = document.createElement("span");
      e.className = "pnav-promo__eyebrow";
      e.textContent = p.eyebrow;
      body.appendChild(e);
    }
    var t = document.createElement("span");
    t.className = "pnav-promo__title";
    t.textContent = p.title || "";
    body.appendChild(t);
    if (p.cta) {
      var c = document.createElement("span");
      c.className = "pnav-promo__cta";
      c.textContent = p.cta + " →";
      body.appendChild(c);
    }
    a.appendChild(body);
    list.appendChild(a);
  }

  /* ── 2c. column sizing ───────────────────────────────────────────────────
     CSS cannot count children. A 6-column menu must divide the row exactly or
     the last column wraps; a 3-column menu must stay left-packed at a natural
     width. Both are the same one-line arithmetic once you know `n`. */
  // 🚨 NEVER hard-code the gap to match the stylesheet. The CSS says 4.4rem;
  //    this theme's root font-size is 10.5px, so the real gap is 46.2px. Six
  //    columns sized against a hard-coded 44 overflowed the row by 11px and the
  //    last column wrapped — with correct-looking inline widths on every column,
  //    which is why it read as a sizing bug rather than an arithmetic one.
  //    Measure the gap; don't assume it.
  var COL_MAX = 240;

  function gapOf(list) {
    var g = parseFloat(getComputedStyle(list).columnGap);
    return isFinite(g) && g > 0 ? g : 44;
  }

  function sizeColumns(details) {
    var list = details.querySelector(".mega-menu__list");
    if (!list) return;
    var cols = list.querySelectorAll(":scope > li");
    var n = cols.length;
    if (!n) return;

    var promo = list.querySelector(".pnav-promo");
    var GAP = gapOf(list);
    // 🚨 On the FIRST open the panel has only just been un-hidden, so the list
    //    can still measure 0 and the arithmetic goes negative — which trips the
    //    "too narrow" guard and leaves the columns un-sized FOREVER (the guard
    //    only re-runs on the next open). Fall back to the panel, then the page.
    // 🚨 clientWidth INCLUDES padding, and `.mega-menu__list` also carries
    //    Dawn's `page-width` (~105px of horizontal padding on desktop). Sizing
    //    six columns against the padded width overflowed the CONTENT box by
    //    ~98px and wrapped the last column, while every column's inline width
    //    looked correct. Subtract the padding — measure the content box.
    var lcs = getComputedStyle(list);
    var pad = (parseFloat(lcs.paddingLeft) || 0) + (parseFloat(lcs.paddingRight) || 0);
    var avail =
      (list.clientWidth || (list.parentElement && list.parentElement.clientWidth) || 0) - pad;
    if (avail <= 0) return;
    if (promo && getComputedStyle(promo).display !== "none") {
      avail -= promo.getBoundingClientRect().width + GAP;
    }
    // Math.floor is not enough on its own — sub-pixel gaps still accumulate over
    // 5 gutters. Take a 1px safety margin per column.
    var w = Math.floor((avail - (n - 1) * GAP) / n) - 1;
    // Never below the CSS min-width — better to wrap than to shred the labels.
    if (w < 130) w = 0;
    var width = w ? Math.min(w, COL_MAX) : 0;
    Array.prototype.forEach.call(cols, function (li) {
      li.style.flex = width ? "0 0 " + width + "px" : "";
      li.style.maxWidth = width ? width + "px" : "";
    });
  }

  function sizeAll() {
    Array.prototype.forEach.call(
      document.querySelectorAll("details.mega-menu"),
      sizeColumns
    );
  }

  /* ── 3. hover to open ───────────────────────────────────────────────────── */
  function bindHover(scope) {
    var fine =
      window.matchMedia &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!fine) return;

    var all = scope.querySelectorAll("details.mega-menu, details.mega-menu-dropdown");
    if (!all.length) return;
    var timer = null;

    function closeAll(except) {
      Array.prototype.forEach.call(all, function (d) {
        if (d !== except && d.open) d.removeAttribute("open");
      });
    }

    Array.prototype.forEach.call(all, function (d) {
      d.addEventListener("mouseenter", function () {
        window.clearTimeout(timer);
        closeAll(d);
        if (!d.open) { d.setAttribute("open", ""); var sm0 = d.querySelector("summary"); send({ ev: "open", kind: "tab", label: sm0 ? sm0.textContent.replace(/\s+/g, " ").trim() : "?" }); }
      });

      d.addEventListener("mouseleave", function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          if (d.open) d.removeAttribute("open");
        }, CLOSE_DELAY);
      });

      // A click on the summary of an ALREADY-hover-opened menu would collapse
      // it — which feels broken, because the pointer is still inside it. Send
      // the click to the section's own destination instead. That is Diego's
      // original complaint: "I have to click Pickleball, then click again."
      var summary = d.querySelector("summary");
      if (summary) {
        summary.addEventListener("click", function (e) {
          if (!d.open) return; // let Dawn open it normally
          var first = d.querySelector(".mega-menu__link--level-2");
          var href = first && first.getAttribute("href");
          if (href) {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = href;
          }
        });
      }
    });

    // Leaving the header entirely closes whatever is open.
    var header = scope.querySelector(".header") || scope;
    header.addEventListener("mouseleave", function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { closeAll(null); }, CLOSE_DELAY);
    });
  }

  /* ── 4. utility bar ─────────────────────────────────────────────────────── */
  var UTIL = [
    { cls: "pnav-util--left", href: "/pages/contact", text: "Customer Support" },
    // Wholesale = the public "become a dealer" guided intake form
    // (/pages/become-a-dealer) that POSTs to the CS desk /cs/api/intake with
    // topic "wholesale" → emails the proton@protonsports.com inbox, Reply-To
    // the dealer. 2026-09-09: Diego — the nav link must NOT drop the public
    // straight onto the direct-order catalog at /pages/wholesale (that URL is
    // frozen for existing accounts who already hold it). See memory
    // wholesale-order-link.md.
    // 2026-09-25 Diego: Ambassador moved OUT of the main nav (was a 6th top-level
    // tab) and up here beside Wholesale, labelled "Become an Ambassador".
    // 2026-09-25 Brant: section renamed "Ambassador" -> "Partner With Us" (hub: ambassador + community/events + wholesale).
    { cls: "pnav-util--right", href: "/pages/partner-with-us", text: "Partner With Us" },
    // 2026-09-25 Diego: a dealer (logged in, or holding their private link on
    // this device) goes STRAIGHT to their order page; everyone else still
    // lands on become-a-dealer. The key comes from the logged-in customer's
    // metafield (window.PROTON_WS_KEY, stamped by theme.liquid) or the
    // pw_access_key the wholesale page saved. A dead key just shows the
    // locked screen — the server still checks every key.
    { cls: "pnav-util--right", href: "/pages/become-a-dealer", text: "Wholesale", wholesale: true },
  ];

  function wholesaleHref(fallback) {
    var k = "";
    try { k = window.PROTON_WS_KEY ? String(window.PROTON_WS_KEY) : ""; } catch (e) {}
    if (!k) { try { k = localStorage.getItem("pw_access_key") || ""; } catch (e) {} }
    return k ? "/pages/wholesale?k=" + encodeURIComponent(k) : fallback;
  }

  function buildUtilityBar() {
    var bar = document.querySelector(".announcement-bar");
    if (!bar || bar.dataset.pnavUtil === "1") return;
    bar.dataset.pnavUtil = "1";
    // links sharing a side sit together in ONE absolutely-positioned group
    var groups = {};
    // 2026-10-13 nav proposal: window.PROTON_NAV.util === "wholesale" ⇒ only the Wholesale link.
    var utilMode = (window.PROTON_NAV && window.PROTON_NAV.util) || "full";
    UTIL.filter(function (u) { return utilMode !== "wholesale" || u.wholesale; }).forEach(function (u) {
      var g = groups[u.cls];
      if (!g) {
        g = groups[u.cls] = document.createElement("span");
        g.className = "pnav-util-group " + u.cls;
        bar.appendChild(g);
      }
      var a = document.createElement("a");
      a.className = "pnav-util";
      a.href = u.wholesale ? wholesaleHref(u.href) : u.href;
      a.textContent = u.text;
      g.appendChild(a);
    });
  }

  /* ── boot ───────────────────────────────────────────────────────────────── */
  function boot() {
    var header = document.querySelector(".header-wrapper") || document;
    try { decorateNew(document); } catch (e) {}
    try {
      Array.prototype.forEach.call(
        header.querySelectorAll("details.mega-menu"),
        function (d) {
          try { addPromo(d); } catch (e) {}
          try { addCta(d); } catch (e) {}
          // 10-16 Diego: a ONE-column panel (Softball) was a full-width black slab with
          // 3 links and dead space ⇒ render it as a compact dropdown card under its tab.
          try {
            var nCols = d.querySelectorAll(".mega-menu__list > li").length;
            d.classList.add("pnav-compact");
          } catch (e) {}
          // 🚨 A closed <details> hides its children natively, so clientWidth is
          //    0 and the arithmetic silently produces zero-width columns. Size
          //    on `toggle` — which fires for hover-open, click-open AND keyboard.
          d.addEventListener("toggle", function () {
            if (!d.open) return;
            sizeColumns(d);
            // measure again once layout has settled — the first frame after a
            // <details> opens can still report a zero-width list
            window.requestAnimationFrame(function () { sizeColumns(d); });
            window.setTimeout(function () { sizeColumns(d); }, 80);
          });
        }
      );
    } catch (e) {}
    try { bindHover(header); } catch (e) {}
    try { buildUtilityBar(); } catch (e) {}
    try { bindClickBeacon(); } catch (e) {}

    var rt = null;
    window.addEventListener("resize", function () {
      window.clearTimeout(rt);
      rt = window.setTimeout(sizeAll, 120);
    });
  }

  /* ── 5. click beacon ─────────────────────────────────────────────────────
     2026-10-13 (Diego: "how many clicks are those getting?") — nothing on the
     site counted nav clicks (Shopify only reports LANDING pages, no GA4).
     Every click on a header/drawer/utility-bar link and every desktop panel
     open / mobile drawer expand is sent with sendBeacon to the Mac mini
     (~/proton-navclicks, launchd com.proton.navclicks, funnel path /navhit).
     Fire-and-forget: a dead endpoint costs the shopper nothing. */
  var BEACON = "https://protons-mac-mini-1.tailccb751.ts.net/navhit";
  function trail(a) {
    var parts = [], el = a.parentElement;
    while (el && el !== document.body) {
      if (el.tagName === "DETAILS") { var sm = el.querySelector(":scope > summary"); if (sm) parts.unshift(sm.textContent.replace(/\s+/g, " ").trim()); }
      el = el.parentElement;
    }
    var col = a.closest(".mega-menu__list > li, .menu-drawer__menu > li > details");
    var head = col && col.querySelector(".mega-menu__link--level-2, summary");
    if (head && parts.indexOf(head.textContent.replace(/\s+/g, " ").trim()) < 0) parts.push(head.textContent.replace(/\s+/g, " ").trim());
    parts.push(a.textContent.replace(/\s+/g, " ").replace(/NEW$/, "").trim());
    return parts.filter(function (p, i) { return p && parts.indexOf(p) === i; }).join(" › ");
  }
  function send(payload) {
    try {
      payload.device = window.matchMedia("(max-width: 989px)").matches ? "mobile" : "desktop";
      payload.page = location.pathname;
      payload.bot = navigator.webdriver ? 1 : 0;
      var body = JSON.stringify(payload);
      if (navigator.sendBeacon) navigator.sendBeacon(BEACON, new Blob([body], { type: "text/plain" }));
      else fetch(BEACON, { method: "POST", body: body, keepalive: true, mode: "no-cors" });
    } catch (e) {}
  }
  function bindClickBeacon() {
    if (document.documentElement.dataset.pnavBeacon === "1") return;
    document.documentElement.dataset.pnavBeacon = "1";
    document.addEventListener("click", function (e) {
      var a = e.target && e.target.closest && e.target.closest("a[href], summary");
      if (!a) return;
      var inHeader = a.closest(".header-wrapper, .announcement-bar, .menu-drawer, header-drawer, .utility-bar");
      if (!inHeader) return;
      if (a.tagName === "SUMMARY") {
        var det = a.parentElement;
        // mobile drawer: an expand (not a collapse) of a top-level or column group
        if (!det || det.open || !a.closest(".menu-drawer, header-drawer")) return;
        if (!a.closest(".menu-drawer")) { send({ ev: "open", kind: "menu", label: "Menu" }); return; } // the hamburger itself
        send({ ev: "open", kind: det.closest(".menu-drawer__submenu") ? "panel" : "tab", label: trail(a) });
        return;
      }
      var kind = a.classList.contains("pnav-util") ? "util"
        : a.classList.contains("pnav-cta") ? "cta"
        : a.closest(".pnav-promo") ? "promo"
        : a.closest(".mega-menu__list, .menu-drawer") ? "link"
        : a.closest(".header__icons") ? "icon"
        : a.closest(".header__inline-menu") ? "tab"
        : a.closest(".header__heading, .header__heading-link") ? "logo" : "other";
      send({ ev: "click", kind: kind, label: trail(a), href: a.getAttribute("href") });
    }, true);
  }

  ready(boot);
  // The header is a Shopify section — it re-renders in the theme editor.
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector(".header-wrapper, .announcement-bar")) boot();
  });
})();
